import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Download, Eye } from 'lucide-react';
import { getOrders, downloadOrderPdf, downloadFile, type OrderListItem } from '@/services/ordersApi';
import { toast } from '@/hooks/use-toast';

const OrdersList = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadOrders();
  }, [page]);

  const loadOrders = async () => {
    if (!token) {
      console.log('No token available for loading orders');
      return;
    }

    try {
      setLoading(true);
      console.log('Loading orders with token:', token ? 'Token present' : 'No token');
      const response = await getOrders(token, page, 10);
      console.log('Orders loaded successfully:', response);
      setOrders(response.items);
      setTotalPages(Math.ceil(response.total / 10));
    } catch (error) {
      console.error('Error loading orders:', error);
      
      // Check if it's a network error (endpoint doesn't exist)
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        toast({
          title: "Fonctionnalité en cours de développement",
          description: "L'API des commandes n'est pas encore disponible sur le serveur",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erreur",
          description: `Impossible de charger vos commandes: ${error.message}`,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadOrder = async (orderId: number, orderName: string) => {
    if (!token) return;

    try {
      const blob = await downloadOrderPdf(token, orderId);
      downloadFile(blob, `${orderName}.pdf`);
      toast({
        title: "Téléchargement réussi",
        description: `Le bon de commande ${orderName} a été téléchargé`,
      });
    } catch (error) {
      console.error('Error downloading order:', error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le bon de commande",
        variant: "destructive",
      });
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD',
    }).format(price);
  };

  const getStateVariant = (color: string): "default" | "destructive" | "secondary" | "outline" => {
    switch (color) {
      case 'yellow':
        return 'outline';
      case 'green':
        return 'secondary';
      case 'red':
        return 'destructive';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Mes Commandes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mes Commandes</CardTitle>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            Vous n'avez encore passé aucune commande.
          </p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Paiement</TableHead>
                  <TableHead>Livraison</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-medium">{order.name}</TableCell>
                    <TableCell>{formatDate(order.date)}</TableCell>
                    <TableCell>{formatPrice(order.amount)}</TableCell>
                    <TableCell>
                      <Badge variant={getStateVariant(order.state_color)}>
                        {order.state_label}
                      </Badge>
                    </TableCell>
                    <TableCell>{order.payment_method_label}</TableCell>
                    <TableCell>{order.delivery_method_label}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadOrder(order.id, order.name)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Précédent
                </Button>
                <span className="flex items-center px-4">
                  Page {page} sur {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Suivant
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OrdersList;